import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    DocumentChange,
    DocumentStatus,
    SalesDocumentType,
    SalesDocument,
} from '@icc/b2b/shared';
import { DialogRef, DIALOG_DATA } from '@icc/ui';
import { DocumentManagerService } from '../document-manager.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { SyncService } from '../sync.service';
import { getSalesDocument } from '../+state/sales-documents.selectors';
import { logger } from '@icc/shared/helpers/b2b';

@Component({
    selector: 'icc-document-history-dialog',
    templateUrl: './document-history-dialog.component.html',
    styleUrls: ['./document-history-dialog.component.scss'],
})
export class DocumentHistoryDialogComponent implements OnInit {
    allChanges: DocumentChange[] = [];

    changes: (
        | (DocumentChange & {
              kind: 'main';
          })
        | {
              group: DocumentChange[];
              extended: boolean;
              kind: 'group';
          }
    )[] = [];

    canOpen = new Map<string, boolean>();
    loading = true;
    baseOfferDocument: SalesDocument | null = null;

    constructor(
        @Inject(DIALOG_DATA)
        public data: {
            document: SalesDocument;
            salesDocument: SalesDocument;
            documentStatusMap: Record<number, DocumentStatus>;
            side: 'buyer' | 'seller';
            groupingSchemaId: number;
            sortingMode: string;
        },
        private router: Router,
        private dialogRef: DialogRef,
        private http: HttpClient,
        private syncService: SyncService,
    ) {}

    ngOnInit(): void {
        this.loadChanges();
        this.loadBaseOffer();
    }

    async loadBaseOffer() {
        // Sprawdź czy dokument to zamówienie z rootId (czyli utworzone na podstawie oferty)
        if (this.data.document.salesDocumentTypeId === SalesDocumentType.Order &&
            this.data.document.rootId) {
            try {
                // Pobierz dokument bazowy
                const documentId = (this.data.salesDocument ?? this.data.document).rootId;
                let document = await this.syncService.getOrLoadDocument(documentId, getSalesDocument);
                if (!document) {
                    await this.syncService.addCurrentDocumentId(documentId);
                    await this.syncService.waitForSynced([documentId]);
                    await this.syncService.loadDocuments([documentId]);
                }
                document = await this.syncService.getOrLoadDocument(documentId, getSalesDocument);
                if (document && document.salesDocumentTypeId === SalesDocumentType.Quotation) {
                    this.baseOfferDocument = document;
                }
            } catch (error) {
                logger.log('Nie udało się pobrać oferty bazowej:', error);
            }
        }
    }

    async loadChanges() {
        const result = await lastValueFrom(
            this.http.get<{
                changes: DocumentChange[];
                documents: SalesDocument[];
            }>('/api/b2b/document-history/' + this.data.document.uuid + '?groupingSchemaId=' + this.data.groupingSchemaId + '&sortingMode=' + this.data.sortingMode)
        );
        if (result.changes) {
            this.loading = false;
            this.allChanges = result.changes;
            result.documents.forEach((document) => {
                this.canOpen.set(document.uuid, (this.data.side === 'seller' ||
                    document.salesDocumentTypeId === SalesDocumentType.Order ||
                    document.salesDocumentTypeId === SalesDocumentType.ServiceOrder) && document.isDeleted === false);
            });
            this.groupChanges();
        }
    }

    groupChanges() {
        this.changes = [];
        for (const change of this.allChanges) {
            if (change.main) {
                this.changes.push({
                    ...change,
                    kind: 'main',
                });
            } else {
                const lastChange = this.changes[this.changes.length - 1];
                if (lastChange && lastChange.kind === 'group') {
                    lastChange.group.push(change);
                } else {
                    this.changes.push({
                        group: [change],
                        extended: false,
                        kind: 'group',
                    });
                }
            }
        }
    }

    getStatus(document: SalesDocument) {
        return this.data.documentStatusMap[Number(document?.statusId)];
    }

    close() {
        this.dialogRef.close();
    }

    open(documentId: SalesDocument['uuid']) {
        this.router.navigate(['trade', 'offer', documentId]);
        this.dialogRef.close();
    }

    openBaseOffer() {
        if (this.baseOfferDocument) {
            this.router.navigate(['trade', 'offer', this.baseOfferDocument.uuid]);
            this.dialogRef.close();
        }
    }

    formatBaseOfferDocument(document: SalesDocument): string {
        return document.documentNumber.join('');
    }
}
